import React, { useState } from 'react';
import { X, User, Phone, Clock, CheckCircle, XCircle, ArrowRight, Image } from 'lucide-react';
import { getPrescriptionService } from '../../../../domains/prescriptions';
import {
  ORDER_STATUS,
  STATUS_LABELS,
} from '../../../constants/orderStatus';
import InputModal from './InputModal';
import styles from './OrderDetailModal.module.css';

/**
 * 주문 상세 모달
 */
const OrderDetailModal = ({ order, onClose, onUpdate }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [inputModal, setInputModal] = useState(null);

  if (!order) return null;

  const handleBackdropClick = e => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // 상태 변경 실행
  const executeStatusChange = async (newStatus, additionalData = {}) => {
    try {
      setIsLoading(true);
      const prescriptionService = getPrescriptionService();
      const data = { status: newStatus, ...additionalData };

      const response = await prescriptionService.updateOrderStatus(order.order_id, data);

      if (response.result_code === '200') {
        const updatedOrder = { ...order, status: newStatus };
        if (data.total_amount) updatedOrder.total_amount = data.total_amount;
        onUpdate(updatedOrder);

        if (newStatus === ORDER_STATUS.PICKED_UP) {
          setTimeout(() => onClose(), 500);
        }
      }
    } catch (error) {
      console.error('상태 변경 실패:', error);
      alert('상태 변경에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  // 상태 변경 핸들러
  const handleStatusChange = newStatus => {
    if (newStatus === ORDER_STATUS.APPROVED) {
      setInputModal({
        title: '결제 금액 입력',
        label: '결제 금액',
        placeholder: '금액을 입력하세요',
        type: 'number',
        confirmText: '승인',
        confirmColor: '#2196F3',
        onConfirm: value => {
          setInputModal(null);
          executeStatusChange(newStatus, { total_amount: value });
        },
      });
      return;
    }

    if (newStatus === ORDER_STATUS.REJECTED) {
      setInputModal({
        title: '거절 사유 입력',
        label: '거절 사유',
        placeholder: '거절 사유를 입력하세요',
        type: 'text',
        confirmText: '거절',
        confirmColor: '#F44336',
        onConfirm: value => {
          setInputModal(null);
          executeStatusChange(newStatus, { rejection_reason: value });
        },
      });
      return;
    }

    executeStatusChange(newStatus);
  };

  const getAvailableActions = () => {
    switch (order.status) {
      case ORDER_STATUS.PENDING:
        return [
          { label: '승인', status: ORDER_STATUS.APPROVED, icon: <CheckCircle />, color: '#2196F3' },
          { label: '거절', status: ORDER_STATUS.REJECTED, icon: <XCircle />, color: '#F44336' },
        ];
      case ORDER_STATUS.APPROVED:
        return []; // 결제 대기중 - 앱에서 결제 완료 후 다음 단계 진행
      case ORDER_STATUS.PAID:
        return [{ label: '조제 시작', status: ORDER_STATUS.PREPARING, icon: <CheckCircle />, color: '#9C27B0' }];
      case ORDER_STATUS.PREPARING:
        return [{ label: '조제 완료', status: ORDER_STATUS.COMPLETED, icon: <CheckCircle />, color: '#4CAF50' }];
      case ORDER_STATUS.COMPLETED:
        return [{ label: '픽업 완료', status: ORDER_STATUS.PICKED_UP, icon: <CheckCircle />, color: '#9E9E9E' }];
      default:
        return [];
    }
  };

  const availableActions = getAvailableActions();
  const hasPrescriptionImage = !!order.prescription_image_url;

  return (
    <>
      <div className={styles.modalOverlay} onClick={handleBackdropClick}>
        <div className={styles.modalContent}>
          <div className={styles.modalHeader}>
            <div>
              <h2>주문 상세</h2>
              <div className={styles.orderInfo}>
                <div className={styles.statusBadge} data-status={order.status}>
                  {STATUS_LABELS[order.status] || order.status}
                </div>
                <p className={styles.orderNumber}>#{order.order_id}</p>
              </div>
            </div>
            <button className={styles.closeButton} onClick={onClose}>
              <X size={24} />
            </button>
          </div>

          <div className={styles.modalBody}>
            <div className={`${styles.contentLayout} ${!hasPrescriptionImage ? styles.singleColumn : ''}`}>
              {hasPrescriptionImage && (
                <div className={styles.leftSection}>
                  <section className={styles.section}>
                    <h3 className={styles.sectionTitle}><Image /> 처방전</h3>
                    <div className={styles.prescriptionImage}>
                      <img
                        src={order.prescription_image_url}
                        alt="처방전"
                        onError={e => {
                          e.target.onerror = null;
                          e.target.src = 'https://via.placeholder.com/600x400/e3f2fd/1976d2?text=No+Image';
                        }}
                      />
                    </div>
                    {order.prescription_id && (
                      <div className={styles.prescriptionInfo}>
                        <div className={styles.infoItem}>
                          <span className={styles.label}>처방전 ID</span>
                          <span className={styles.value}>#{order.prescription_id}</span>
                        </div>
                      </div>
                    )}
                  </section>
                </div>
              )}

              <div className={styles.rightSection}>
                <section className={styles.section}>
                  <h3 className={styles.sectionTitle}><User /> 환자 정보</h3>
                  <div className={styles.infoGrid}>
                    <div className={styles.infoItem}>
                      <span className={styles.label}>이름</span>
                      <span className={styles.value}>{order.user?.name || '-'}</span>
                    </div>
                    <div className={styles.infoItem}>
                      <span className={styles.label}><Phone size={16} /> 연락처</span>
                      <span className={styles.value}>{order.user?.phone_number || '-'}</span>
                    </div>
                  </div>
                </section>

                <section className={styles.section}>
                  <h3 className={styles.sectionTitle}>결제 정보</h3>
                  <div className={styles.paymentInfo}>
                    <div className={styles.paymentRow}>
                      <span>결제 금액</span>
                      <span>
                        <strong>
                          {order.total_amount ? `${order.total_amount.toLocaleString()}원` : '미정'}
                        </strong>
                      </span>
                    </div>
                  </div>
                </section>

                <section className={styles.section}>
                  <h3 className={styles.sectionTitle}><Clock /> 시간 정보</h3>
                  <div className={styles.infoGrid}>
                    <div className={styles.infoItem}>
                      <span className={styles.label}>주문 시간</span>
                      <span className={styles.value}>{new Date(order.created_at).toLocaleString('ko-KR')}</span>
                    </div>
                    <div className={styles.infoItem}>
                      <span className={styles.label}>최근 수정</span>
                      <span className={styles.value}>{new Date(order.updated_at).toLocaleString('ko-KR')}</span>
                    </div>
                  </div>
                </section>
              </div>
            </div>
          </div>

          {availableActions.length > 0 && (
            <div className={styles.modalFooter}>
              {availableActions.map((action, index) => (
                <button
                  key={index}
                  className={styles.actionButton}
                  style={{ backgroundColor: action.color }}
                  onClick={() => handleStatusChange(action.status)}
                  disabled={isLoading}
                >
                  {action.icon}
                  <span>{isLoading ? '처리중...' : action.label}</span>
                  <ArrowRight />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {inputModal && (
        <InputModal
          title={inputModal.title}
          label={inputModal.label}
          placeholder={inputModal.placeholder}
          type={inputModal.type}
          confirmText={inputModal.confirmText}
          confirmColor={inputModal.confirmColor}
          onConfirm={inputModal.onConfirm}
          onCancel={() => setInputModal(null)}
        />
      )}
    </>
  );
};

export default OrderDetailModal;
